import { ProductionSegmentType } from "types/types";
import { useVisibleProcessIds } from "utils/customHooks/genericHooks";
import { useSelector } from "store/useSelector";
import { uniq } from "lodash";
import { objectFromEntries } from "@raven/shared/utils";

const DATA_SOURCES = "Data Source Groups";

/** explanations & traceability field on all segments visible on the page
 *
 * * [Metadata](https://gitlab.com/raventelemetry/services/askr/-/blob/main/doc/arch/adr-0017-askr-metadata.md) is subject to change without notice, and should be treated as a black box by consumers.
 * * Consumers should not create logic that depends on specific values in the metadata fields.
 */
export function useVisibleSegmentsMetadata(): {
  [metadataName: string]: any[];
} {
  const visibleSegments = useVisibleSegments();
  const allMetadataObject = visibleSegments.reduce(
    (acc, cur) => {
      // capture any metadata fields from the segments
      const metadataEntries = cur.meta && Object.entries(cur.meta);
      if (metadataEntries && metadataEntries.length > 0) {
        const arrayMetadataEntries = metadataEntries.filter(
          ([metaTitle, metaValue]) => Array.isArray(metaValue)
        );
        // spread all metadata values into the associated value in the accumulator
        arrayMetadataEntries.forEach(([metaTitle, metaValue]) => {
          acc = {
            ...acc,
            [metaTitle]: [...(acc[metaTitle] || []), ...metaValue],
          };
        });
      }

      // also capture datasourceGroup.name e.g. "Hublet" under "Data Sources"
      const tagEvents = cur.events;
      if (tagEvents && tagEvents.length > 0) {
        tagEvents.forEach(event => {
          const datasourceName = event.datasourceGroup?.name; // e.g. "Hublet"
          if (datasourceName) {
            acc = {
              ...acc,
              [DATA_SOURCES]: [...(acc[DATA_SOURCES] || []), datasourceName],
            };
          }
        });
      }

      return acc;
    },
    {} as { [metadataName: string]: any[] }
  ); // e.g. {traceability: [], metadata: []}

  const allUniqueMetadataObject = objectFromEntries(
    Object.entries(allMetadataObject).map(([metaTitle, metaValue]) => [
      metaTitle,
      uniq(metaValue),
    ])
  );

  // add tag > events > datasourceGroup from all visible segments

  return allUniqueMetadataObject as {
    [metadataName: string]: any[];
  };
}

/** segments which are visible in any Timelines on this page */
function useVisibleSegments() {
  const allSegments = useSelector(state => state.legacy.segments);
  const visibleProcessIds = useVisibleProcessIds();
  const visibleSegments = Object.entries(allSegments).reduce(
    (acc, [processId, processSegments]) => {
      if (visibleProcessIds.includes(processId)) {
        return [...acc, ...processSegments];
      }
      return acc;
    },
    [] as ProductionSegmentType[]
  );
  return visibleSegments;
}
